<template>
  <div>
    <b-modal
      hide-footer
      size="lg"
      title="Sync Event Flag"
      centered
      id="sync-event-flag"
      @show="handleShow"
      @hide="handleClose"
    >
      <div style="min-height: 300px" class="text-black">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <span :class="selectedEventIds.length >= 200 ? 'text-danger small font-weight-bold' : 'text-muted small'">
            {{ selectedEventIds.length }}/200 event(s) selected
          </span>
          <div>
            <b-button variant="secondary" class="mr-1" @click="handleClose">
              Cancel
            </b-button>
            <b-button
              :disabled="loadingSyncFlag || !selectedEventIds.length"
              variant="primary"
              style="min-width: 130px"
              @click="handleSyncFlag"
            >
              <Spinner v-if="loadingSyncFlag" />
              <template v-else>
                <feather-icon icon="ZapIcon" style="margin-right: 4px" />
                Sync Flag
              </template>
            </b-button>
          </div>
        </div>

        <b-form-input
          v-model="searchEventTitle"
          placeholder="Search by Event Title..."
          class="mb-1"
          @input="handleSearchInput"
        />

        <div
          ref="eventList"
          style="max-height: 500px; overflow-y: auto"
          @scroll="handleScroll"
        >
          <div
            v-if="loadingEvents && events.length === 0"
            class="text-center py-3"
          >
            <Spinner />
          </div>

          <div
            v-for="(event, index) in events"
            :key="event.eventId"
            class="d-flex align-items-start p-1"
            :class="{
              'border-bottom': index !== events.length - 1,
              'bg-selected': selectedEventIds.includes(event.eventId),
            }"
            style="cursor: pointer; gap: 10px"
            @click="toggleSelect(event)"
          >
            <b-form-checkbox
              :checked="selectedEventIds.includes(event.eventId)"
              style="pointer-events: none; margin-top: 2px"
            />
            <div class="flex-1 w-100">
              <div
                class="d-flex align-items-center justify-content-between"
                style="gap: 8px"
              >
                <p class="font-weight-bold mb-0">{{ event.eventTitle }}</p>
                <span class="text-muted small text-nowrap">
                  <feather-icon icon="ClockIcon" />
                  {{ formatTime(event.startTime, "dd MMM yyyy HH:mm") }}
                </span>
              </div>

              <div class="d-flex mt-50" style="gap: 20px">
                <div
                  v-for="team in getSortedTeams(event.teams)"
                  :key="team.teamId"
                  class="d-flex align-items-center"
                  style="gap: 8px"
                >
                  <b-badge
                    :variant="team.role === 'Home' ? 'primary' : 'secondary'"
                    pill
                    style="font-size: 10px; min-width: 40px; text-align: center"
                  >
                    {{ team.role }}
                  </b-badge>
                  <div class="team-flag-wrapper" :class="{ 'no-flag': !team.flagUrl }">
                    <img
                      v-if="team.flagUrl"
                      :src="team.flagUrl"
                      :alt="team.name"
                      class="team-flag-img"
                    />
                    <div v-else class="team-flag-placeholder">
                      <feather-icon icon="ImageIcon" style="width: 18px; height: 18px; color: #adb5bd" />
                    </div>
                  </div>
                  <span class="small font-weight-bold">{{ team.name || team.teamId }}</span>
                  <b-badge
                    v-if="!team.flagUrl"
                    variant="light-warning"
                    pill
                    style="font-size: 10px"
                  >
                    No flag
                  </b-badge>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="loadingEvents && events.length > 0"
            class="text-center py-2"
          >
            <Spinner />
          </div>

          <p
            v-if="!loadingEvents && events.length === 0"
            class="text-center text-muted py-3 mb-0"
          >
            No events found.
          </p>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BBadge, BFormCheckbox, BFormInput } from "bootstrap-vue";
import { mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";
import { formatTime } from "@/helpers/utils";
import Spinner from "@/@core/components/Spinner.vue";

export default {
  components: {
    BModal,
    BButton,
    BBadge,
    BFormCheckbox,
    BFormInput,
    Spinner,
  },
  data() {
    return {
      events: [],
      page: 1,
      limit: 20,
      total: 0,
      loadingEvents: false,
      loadingSyncFlag: false,
      selectedEventIds: [],
      searchEventTitle: "",
      searchDebounceTimer: null,
    };
  },
  methods: {
    ...mapActions({
      getListEventOfLeague: "sport/list/getListEventOfLeague",
      syncSportEventTeamFlagUrls: "sport/create/syncSportEventTeamFlagUrls",
    }),
    async handleShow() {
      this.events = [];
      this.page = 1;
      this.total = 0;
      this.selectedEventIds = [];
      this.searchEventTitle = "";
      await this.fetchEvents();
    },
    handleSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.events = [];
        this.page = 1;
        this.total = 0;
        this.fetchEvents();
      }, 400);
    },
    async fetchEvents() {
      if (this.loadingEvents) return;
      this.loadingEvents = true;
      try {
        const startTimeFrom = new Date().toISOString();
        const startTimeTo = new Date("2099-12-31T23:59:59Z").toISOString();
        const params = {
          startTimeFrom,
          startTimeTo,
          limit: this.limit,
          page: this.page,
        };
        if (this.searchEventTitle.trim()) {
          params.eventTitle = (this.searchEventTitle.trim());
        }
        const res = await this.getListEventOfLeague(params);
        if (res && res.data) {
          this.events = [...this.events, ...res.data];
          this.total = res.pagination?.total || 0;
        }
      } finally {
        this.loadingEvents = false;
      }
    },
    handleScroll(e) {
      const el = e.target;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (nearBottom && !this.loadingEvents && this.events.length < this.total) {
        this.page += 1;
        this.fetchEvents();
      }
    },
    toggleSelect(event) {
      if (this.selectedEventIds.includes(event.eventId)) {
        this.selectedEventIds = this.selectedEventIds.filter(
          (id) => id !== event.eventId
        );
      } else {
        if (this.selectedEventIds.length >= 200) {
          this.showToastError(false, "You can select a maximum of 200 events.");
          return;
        }
        this.selectedEventIds = [...this.selectedEventIds, event.eventId];
      }
    },
    getSortedTeams(teams) {
      if (!teams || !teams.length) return [];
      return [...teams].sort((a, b) => {
        if (a.role === "Home") return -1;
        if (b.role === "Home") return 1;
        return 0;
      });
    },
    async handleSyncFlag() {
      this.loadingSyncFlag = true;
      const res = await this.syncSportEventTeamFlagUrls({
        eventIds: this.selectedEventIds,
      });
      this.loadingSyncFlag = false;
      if (res) {
        const isLarge = this.selectedEventIds.length > 10;
        this.showToastSuccess(
          true,
          isLarge
            ? "Flag sync queued. Data will be updated after ~30 seconds."
            : "Flag sync has been queued successfully!"
        );
        this.handleClose();
      }
    },
    handleClose() {
      this.$bvModal.hide("sync-event-flag");
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastError,
      showToastSuccess,
      formatTime,
    };
  },
};
</script>

<style scoped>
.bg-selected {
  background-color: #d8e6f8 !important;
}

.team-flag-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #dee2e6;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.team-flag-wrapper.no-flag {
  background-color: #f8f9fa;
  border: 1px dashed #adb5bd;
}

.team-flag-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.team-flag-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
</style>
